import type {
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesApi,
  ISeriesPrimitive,
  PrimitivePaneViewZOrder,
  SeriesAttachedParameter,
  SeriesType,
  Time,
} from 'lightweight-charts';

type RenderTarget = Parameters<NonNullable<IPrimitivePaneRenderer['drawBackground']>>[0];

export interface ProfileBin {
  low: number;
  high: number;
  volume: number;
  inValueArea: boolean;
}

export interface ProfileLine {
  price: number;
  kind: 'poc' | 'vah' | 'val' | 'lvn' | 'hvn';
  label: string;
}

export interface ProfileDrawing {
  bins: ProfileBin[];
  lines: ProfileLine[];
  /** Fraction of the chart width the widest bar takes. */
  width: number;
  colors: {
    bar: string;
    barValue: string;
    poc: string;
    edge: string;
    lvn: string;
    hvn: string;
    label: string;
  };
}

/**
 * The volume profile, drawn as horizontal bars against the price scale.
 *
 * Anchored to the RIGHT edge and growing leftward, which is the convention and
 * also the only side that works: the profile describes the present state of the
 * range, so it belongs beside the newest candles rather than over the oldest.
 *
 * Bars sit beneath the candles and level lines above them; see the constructor
 * for why the two passes are split that way.
 */
export class VolumePrimitive implements ISeriesPrimitive<Time> {
  private series: ISeriesApi<SeriesType> | null = null;
  private requestUpdate: (() => void) | null = null;
  private drawing: ProfileDrawing | null = null;
  private readonly views: IPrimitivePaneView[];

  constructor() {
    /*
     * Two passes, and the split is the whole design.
     *
     * The histogram is drawn BENEATH the series: it is context for the price
     * action, and candles painted over it read correctly. The level lines are
     * drawn ABOVE, because a line beneath the candles vanishes exactly where
     * price has traded — which is where it most needs to be visible.
     */
    const background: IPrimitivePaneRenderer = {
      draw: () => {},
      drawBackground: (target: RenderTarget) => this.renderBars(target),
    };
    const foreground: IPrimitivePaneRenderer = {
      draw: (target: RenderTarget) => {
        this.renderLines(target);
        this.renderLabels(target);
      },
    };

    this.views = [
      { zOrder: (): PrimitivePaneViewZOrder => 'bottom', renderer: () => background },
      { zOrder: (): PrimitivePaneViewZOrder => 'top', renderer: () => foreground },
    ];
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this.series = param.series as ISeriesApi<SeriesType>;
    this.requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this.series = null;
    this.requestUpdate = null;
  }

  setDrawing(drawing: ProfileDrawing | null): void {
    this.drawing = drawing;
    this.requestUpdate?.();
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return this.views;
  }

  private renderBars(target: RenderTarget): void {
    const series = this.series;
    const drawing = this.drawing;
    if (!series || !drawing || drawing.bins.length === 0) return;

    let peak = 0;
    for (const b of drawing.bins) if (b.volume > peak) peak = b.volume;
    if (peak <= 0) return;

    target.useBitmapCoordinateSpace(({ context, bitmapSize, verticalPixelRatio }) => {
      const maxWidth = bitmapSize.width * drawing.width;

      for (const bin of drawing.bins) {
        const top = series.priceToCoordinate(bin.high);
        const bottom = series.priceToCoordinate(bin.low);
        if (top === null || bottom === null) continue;

        const y = top * verticalPixelRatio;
        // A one-pixel gap keeps adjacent bins legible as separate rows without
        // a stroke, which at this size would be most of the bar.
        const h = Math.max(1, (bottom - top) * verticalPixelRatio - 1);
        const w = (bin.volume / peak) * maxWidth;
        if (w < 0.5) continue;

        context.save();
        context.fillStyle = bin.inValueArea ? drawing.colors.barValue : drawing.colors.bar;
        context.fillRect(bitmapSize.width - w, y, w, h);
        context.restore();
      }
    });
  }

  private renderLines(target: RenderTarget): void {
    const series = this.series;
    const drawing = this.drawing;
    if (!series || !drawing) return;

    target.useBitmapCoordinateSpace(({ context, bitmapSize, horizontalPixelRatio, verticalPixelRatio }) => {
      for (const line of drawing.lines) {
        const y = series.priceToCoordinate(line.price);
        if (y === null) continue;
        const py = Math.round(y * verticalPixelRatio) + 0.5;

        context.save();
        context.strokeStyle = colourFor(line.kind, drawing.colors);
        context.lineWidth = Math.max(1, Math.round((line.kind === 'poc' ? 2 : 1.25) * horizontalPixelRatio));
        // Dashed for the value edges: they bound a region rather than marking a
        // single traded price, and a solid line claims more precision than that.
        if (line.kind === 'vah' || line.kind === 'val') context.setLineDash([5, 4]);
        context.beginPath();
        context.moveTo(0, py);
        context.lineTo(bitmapSize.width, py);
        context.stroke();
        context.restore();
      }
    });
  }

  private renderLabels(target: RenderTarget): void {
    const series = this.series;
    const drawing = this.drawing;
    if (!series || !drawing) return;

    target.useMediaCoordinateSpace(({ context }) => {
      context.save();
      context.font = '600 10px ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
      context.textBaseline = 'middle';

      for (const line of drawing.lines) {
        const y = series.priceToCoordinate(line.price);
        if (y === null) continue;
        const w = context.measureText(line.label).width;

        // Left edge, opposite the bars: a label over the histogram would sit on
        // the one part of the chart the profile has already filled.
        context.fillStyle = 'rgba(0, 0, 0, 0.68)';
        context.fillRect(6, y - 7, w + 7, 14);
        context.fillStyle = colourFor(line.kind, drawing.colors);
        context.fillText(line.label, 9.5, y);
      }
      context.restore();
    });
  }
}

function colourFor(kind: ProfileLine['kind'], colors: ProfileDrawing['colors']): string {
  if (kind === 'poc') return colors.poc;
  if (kind === 'lvn') return colors.lvn;
  if (kind === 'hvn') return colors.hvn;
  return colors.edge;
}
